import { API_CONFIG, HTTP_STATUS } from '../config/api.config';
import authService from './auth.service';

class HttpService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS;
    this.retryDelay = API_CONFIG.RETRY_DELAY;
  }

  // Méthode principale pour les requêtes HTTP
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        // ...authService.getAuthHeaders(), // Désactivé pour permettre l'accès sans authentification
        ...options.headers,
      },
      ...options,
    };

    // Ajouter le timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    config.signal = controller.signal;

    try {
      const response = await this.makeRequest(url, config);
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  // Effectuer la requête avec retry logic
  async makeRequest(url, config, attempt = 1) {
    try {
      const response = await fetch(url, config);
      
      // Gérer les erreurs d'authentification (désactivé)
      // if (response.status === HTTP_STATUS.UNAUTHORIZED && attempt === 1) {
      //   try {
      //     await authService.refreshToken();
      //     // Retenter avec le nouveau token
      //     config.headers = {
      //       ...config.headers,
      //       ...authService.getAuthHeaders(),
      //     };
      //     return await this.makeRequest(url, config, attempt + 1);
      //   } catch (refreshError) {
      //     // Si le refresh échoue, rediriger vers la page de connexion
      //     authService.clearAuthData();
      //     window.location.href = '/login';
      //     throw new Error('Session expirée');
      //   }
      // }

      // Gérer les autres erreurs
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur HTTP détaillée:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData: errorData
        });
        if (errorData.reference_chantier) {
          alert(errorData.reference_chantier[0]);
        }
        
        // Créer une erreur plus détaillée
        const error = new Error(errorData.detail || errorData.message || `Erreur HTTP: ${response.status}`);
        error.status = response.status;
        error.statusText = response.statusText;
        error.url = response.url;
        error.response = errorData;
        throw error;
      }

      return response;
    } catch (error) {
      // Retry logic pour les erreurs réseau
      if (attempt < this.retryAttempts && this.isRetryableError(error)) {
        await this.delay(this.retryDelay * attempt);
        return await this.makeRequest(url, config, attempt + 1);
      }
      
      throw error;
    }
  }

  // Vérifier si l'erreur est retryable
  isRetryableError(error) {
    return error.name === 'AbortError' || 
           error.message.includes('network') ||
           error.message.includes('timeout');
  }

  // Délai pour le retry
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Méthodes HTTP
  async get(endpoint, options = {}) {
    const response = await this.request(endpoint, { ...options, method: 'GET' });
    return response.json();
  }

  async post(endpoint, data, options = {}) {
    const response = await this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async put(endpoint, data, options = {}) {
    const response = await this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async patch(endpoint, data, options = {}) {
    const response = await this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response.json();
  }

  async delete(endpoint, options = {}) {
    const response = await this.request(endpoint, { ...options, method: 'DELETE' });
    return response.json();
  }

  // Upload de fichiers
  async uploadFile(endpoint, formData, options = {}) {
    const response = await this.request(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        // ...authService.getAuthHeaders(), // Désactivé pour permettre l'accès sans authentification
        // Ne pas définir Content-Type pour FormData
      },
      body: formData,
    });
    
    return response.json();
  }
}

// Instance singleton
export const httpService = new HttpService();
export default httpService; 